import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminGet, adminPost, formatApiError } from "../../lib/adminApi";
import { toast } from "sonner";
import { useUiPreference } from "../../lib/uiPreferences";
import { ErrorBanner, OvergeslagenBanner, PaginationBar, formatDate } from "../../components/admin/AdminUI";
import ViewModeToggle from "../../components/admin/ViewModeToggle";
import { WERKNEMER_STATUSES, WERKNEMER_STATUS_LABELS } from "../../lib/constants";
import { uitnodigingKolomLabel } from "../../lib/namen";
import { Button, Card, Input, Label, PageHeader, StatusBadge } from "../../components/fulkra";
import { TH_GROEP, TD_GROEP, RIJ } from "../../components/fulkra/tabel";

/**
 * Werknemers in de huisstijl.
 *
 * Restyling, geen herbouw: dezelfde filters, dezelfde groepering per werkgever, dezelfde
 * twee weergaven en alle data-testid's ongewijzigd.
 */

const KEUZEVELD = {
  width: 224, height: "var(--veld-hoogte-desktop)",
  borderRadius: "var(--radius)", border: "1px solid var(--border-subtle)",
  background: "var(--wit)", color: "var(--text-strong)",
  padding: "0 var(--ruimte-5)", fontSize: "var(--fs-body)", fontFamily: "var(--font-tekst)",
  boxShadow: "var(--schaduw-1)",
};



const DETAILLINK = { fontSize: "var(--fs-xs)", fontWeight: "var(--fw-semi)", color: "var(--text-action)" };
/* De link naar het werkgever-detail staat in het ontwerp een tint lichter dan de
   rij-actie eronder. */
const GROEPLINK = { fontSize: "var(--fs-xs)", fontWeight: "var(--fw-medium)", color: "var(--text-action)" };

export default function WerknemersPage() {
  /* De zoekbalk in de kopbalk stuurt hierheen met ?q=… — die term nemen we over als
     beginwaarde, zodat zoeken vanuit de kopbalk ook echt iets doet. */
  const [zoekParams] = useSearchParams();
  const [search, setSearch] = useState(zoekParams.get("q") || "");

  React.useEffect(() => {
    const uitAdres = zoekParams.get("q") || "";
    setSearch((huidig) => (uitAdres && uitAdres !== huidig ? uitAdres : huidig));
  }, [zoekParams]);
  const [status, setStatus] = useState("");
  const [werkgever, setWerkgever] = useState("");
  const [offset, setOffset] = useState(0);
  const [view, setView] = useUiPreference("werknemers_view", "rijen");
  const [nieuwOpen, setNieuwOpen] = useState(false);
  const qc = useQueryClient();
  const LIMIT = 100;

  const werkgeversQ = useQuery({
    queryKey: ["werkgevers", "all"],
    queryFn: () => adminGet("/api/admin/werkgevers", { limit: 100 }),
  });

  const aanmaken = useMutation({
    mutationFn: (body) => adminPost("/api/admin/werknemers", body),
    onSuccess: (nieuw) => {
      setNieuwOpen(false);
      toast.success(`${nieuw.klantnummer} aangemaakt`);
      // Ook de werkgeverslijst verversen: bij een zzp'er is er zojuist een regel bijgekomen.
      qc.invalidateQueries({ queryKey: ["werknemers"] });
      qc.invalidateQueries({ queryKey: ["werkgevers"] });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const params = { limit: LIMIT, offset };
  if (search) params.q = search;
  if (status) params.status = status;
  if (werkgever) params.werkgever_id = werkgever;

  const q = useQuery({
    queryKey: ["werknemers", params],
    queryFn: () => adminGet("/api/admin/werknemers", params),
    keepPreviousData: true,
  });

  const items = q.data?.items || q.data || [];
  const total = q.data?.total ?? items.length;
  /* De server slaat een rij over die niet in het model past en telt hem. Dat hoort
     zichtbaar te zijn: anders staat er "25 resultaten" terwijl er 24 in beeld staan
     en mist er stilzwijgend iemand. */
  const overgeslagen = q.data?.overgeslagen ?? 0;

  const groepen = useMemo(() => {
    const map = new Map();
    for (const w of items) {
      const key = w.werkgever_id || "_geen";
      const naam = w.werkgever_naam || "Geen werkgever";
      if (!map.has(key)) map.set(key, { werkgever_id: key, werkgever_naam: naam, items: [] });
      map.get(key).items.push(w);
    }
    return Array.from(map.values()).sort((a, b) => a.werkgever_naam.localeCompare(b.werkgever_naam, "nl"));
  }, [items]);

  const gefilterd = werkgever ? groepen : groepen;

  return (
    <div data-testid="werknemers-page">
      <PageHeader
        title="Werknemers"
        subtitle="Gegroepeerd per werkgever — gebruik filter om in te zoomen"
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: "var(--ruimte-4)" }}>
            <ViewModeToggle value={view} onChange={setView} testid="werknemers-viewmode" />
            <Button size="sm" style={{ minHeight: 34 }} onClick={() => setNieuwOpen(true)} data-testid="btn-werknemer-nieuw">
              Nieuwe werknemer
            </Button>
          </div>
        }
      />

      <div
        data-testid="werknemers-filters"
        style={{ marginBottom: "var(--ruimte-6)", display: "flex", flexWrap: "wrap", gap: "var(--ruimte-4)" }}
      >
        <select
          value={werkgever}
          onChange={(e) => { setWerkgever(e.target.value); setOffset(0); }}
          aria-label="Werkgever-filter" data-testid="werknemers-werkgever-filter"
          style={KEUZEVELD}
        >
          <option value="">Alle werkgevers</option>
          {(werkgeversQ.data?.items || []).map((w) => (
            <option key={w.werkgever_id} value={w.werkgever_id}>{w.bedrijfsnaam}</option>
          ))}
        </select>

        <div style={{ width: 260 }}>
          <Input
            type="text"
            placeholder="Zoek klantnummer of naam"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
            aria-label="Zoek werknemer"
            data-testid="werknemers-search"
            mobiel={false}
            style={{ height: "var(--veld-hoogte-desktop)", minHeight: "var(--veld-hoogte-desktop)" }}
          />
        </div>

        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setOffset(0); }}
          aria-label="Status-filter" data-testid="werknemers-status-filter"
          style={{ ...KEUZEVELD, width: 200 }}
        >
          <option value="">Alle statussen</option>
          {WERKNEMER_STATUSES.map((s) => <option key={s} value={s}>{WERKNEMER_STATUS_LABELS[s] || s}</option>)}
        </select>
      </div>

      <ErrorBanner error={q.error} />

      <OvergeslagenBanner
        aantal={overgeslagen} enkelvoud="werknemer" meervoud="werknemers"
        testid="werknemers-overgeslagen"
      />

      {q.isLoading ? (
        <Leeg>Laden…</Leeg>
      ) : items.length === 0 ? (
        <Leeg testid="werknemers-empty">Geen werknemers voor deze selectie.</Leeg>
      ) : (
        <div style={{ display: "grid", gap: "var(--ruimte-8)" }}>
          {gefilterd.map((groep) => (
            <WerkgeverGroep key={groep.werkgever_id} groep={groep} view={view} />
          ))}
        </div>
      )}

      <PaginationBar
        offset={offset}
        limit={LIMIT}
        total={total}
        onPrev={() => setOffset(Math.max(0, offset - LIMIT))}
        onNext={() => setOffset(offset + LIMIT)}
      />

      {nieuwOpen ? (
        <NieuweWerknemerModal
          werkgevers={werkgeversQ.data?.items || []}
          gekozenWerkgever={werkgever}
          onClose={() => setNieuwOpen(false)}
          onSubmit={(body) => aanmaken.mutate(body)}
          loading={aanmaken.isPending}
        />
      ) : null}
    </div>
  );
}

function Leeg({ children, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        background: "var(--wit)", border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-vlak)", boxShadow: "var(--elevation-card)",
        padding: "var(--ruimte-10)", textAlign: "center",
        fontSize: "var(--fs-body)", color: "var(--text-soft)",
      }}
    >
      {children}
    </div>
  );
}

function WerkgeverGroep({ groep, view }) {
  return (
    <section
      data-testid={`werknemer-groep-${groep.werkgever_id}`}
      style={{
        background: "var(--wit)", border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius)", boxShadow: "var(--elevation-card)", overflow: "hidden",
      }}
    >
      <header
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--ruimte-5)",
          borderBottom: "1px solid var(--border-subtle)", background: "var(--surface-sunken)",
          padding: "var(--ruimte-5) var(--ruimte-6)",
        }}
      >
        <div>
          <h3 style={{ fontFamily: "var(--font-tekst)", fontWeight: "var(--fw-medium)", fontSize: "var(--fs-body)", lineHeight: 1.3, color: "var(--text-strong)" }}>{groep.werkgever_naam}</h3>
          <p style={{ fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>
            {groep.items.length} werknemer{groep.items.length === 1 ? "" : "s"}
          </p>
        </div>
        {groep.werkgever_id !== "_geen" ? (
          <Link to={`/admin/werkgevers/${groep.werkgever_id}`} style={GROEPLINK}>
            Naar werkgever-detail →
          </Link>
        ) : null}
      </header>

      {view === "blokjes" ? (
        <div
          data-testid={`werknemer-groep-blokjes-${groep.werkgever_id}`}
          style={{
            display: "grid", gap: "var(--ruimte-5)", padding: "var(--ruimte-6)",
            gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
          }}
        >
          {groep.items.map((w) => (
            <WerknemerBlokje key={w.werknemer_id} w={w} />
          ))}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          {/* Vaste kolombreedtes. Elke werkgever krijgt zijn eigen tabel, en een tabel meet
              zijn kolommen normaal op wat erin staat — bij "Otera" met korte namen kwam de
              statuskolom dus ergens anders te staan dan bij "Van Kerkhof Timmerbedrijf".
              Steven, 24-08-2026: "dat wil ik strak getrokken hebben." */}
          <table
            style={{
              width: "100%", borderCollapse: "collapse", tableLayout: "fixed",
              minWidth: 720, fontSize: "var(--fs-body)",
            }}
          >
            <colgroup>
              <col style={{ width: "9rem" }} />
              <col />
              <col style={{ width: "10rem" }} />
              <col style={{ width: "8rem" }} />
              <col style={{ width: "9rem" }} />
              <col style={{ width: "7rem" }} />
            </colgroup>
            <thead>
              <tr>
                {["Klantnr.", "Naam", "Status", "Uitnodiging", "Aangemaakt"].map((k) => (
                  <th key={k} scope="col" style={TH_GROEP}>{k}</th>
                ))}
                <th scope="col" style={{ ...TH_GROEP, textAlign: "right" }}>Actie</th>
              </tr>
            </thead>
            <tbody>
              {groep.items.map((w) => (
                <tr key={w.werknemer_id} data-testid={`werknemer-row-${w.klantnummer}`} style={RIJ}>
                  <td style={{ ...TD_GROEP, fontFamily: "var(--font-mono)", fontSize: "var(--fs-sm)" }}>{w.klantnummer}</td>
                  <td style={TD_GROEP}>
                    {[w.voorletters, w.tussenvoegsel, w.achternaam].filter(Boolean).join(" ")}
                  </td>
                  <td style={TD_GROEP}><StatusBadge value={w.status} data-testid={`status-${w.klantnummer}`} /></td>
                  <td
                    style={{ ...TD_GROEP, fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}
                    data-testid={`uitnodiging-${w.klantnummer}`}
                  >
                    {uitnodigingKolomLabel(w.uitnodiging_status, w.uitnodiging_verzonden_op)}
                  </td>
                  <td style={{ ...TD_GROEP, fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>{formatDate(w.created_at)}</td>
                  <td style={{ ...TD_GROEP, textAlign: "right" }}>
                    <Link
                      to={`/admin/werknemers/${w.werknemer_id}`}
                      data-testid={`btn-werknemer-detail-${w.klantnummer}`}
                      style={DETAILLINK}
                    >
                      Bekijken →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function WerknemerBlokje({ w }) {
  const naam = [w.voorletters, w.tussenvoegsel, w.achternaam].filter(Boolean).join(" ");
  return (
    <article
      data-testid={`werknemer-blokje-${w.klantnummer}`}
      style={{
        display: "flex", flexDirection: "column", gap: "var(--ruimte-4)",
        background: "var(--surface-card)", border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius)", boxShadow: "var(--schaduw-1)",
        padding: "var(--ruimte-6)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--ruimte-4)" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>{w.klantnummer}</div>
          <div style={{ marginTop: 2, fontSize: "var(--fs-body)", fontWeight: "var(--fw-medium)", color: "var(--text-body)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {naam || "—"}
          </div>
        </div>
        <StatusBadge value={w.status} data-testid={`status-blokje-${w.klantnummer}`} />
      </div>
      <div
        data-testid={`uitnodiging-blokje-${w.klantnummer}`}
        style={{ fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}
      >
        Uitnodiging: {uitnodigingKolomLabel(w.uitnodiging_status, w.uitnodiging_verzonden_op)}
      </div>
      <div style={{ marginTop: "auto", fontSize: "var(--fs-xs)", lineHeight: "normal" }}>
        <Link
          to={`/admin/werknemers/${w.werknemer_id}`}
          data-testid={`btn-werknemer-blokje-detail-${w.klantnummer}`}
          style={DETAILLINK}
        >
          Bekijken →
        </Link>
      </div>
    </article>
  );
}

function WerknemerVeld({ naam, label, verplicht, type, placeholder, breed, waarde, onChange, fout }) {
  return (
    <div style={{ display: "grid", gap: "var(--ruimte-3)", gridColumn: breed ? "1 / -1" : "auto" }}>
      <Label htmlFor={`wn-${naam}`} required={verplicht}>{label}</Label>
      <Input
        id={`wn-${naam}`}
        type={type}
        value={waarde}
        onChange={(e) => onChange(naam, e.target.value)}
        data-testid={`modal-werknemer-${naam}`}
        placeholder={placeholder}
        aria-invalid={fout ? true : undefined}
      />
      {fout ? (
        <span data-testid={`fout-${naam}`} style={{ fontSize: "var(--fs-xs)", color: "var(--text-alarm)" }}>
          {fout}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Eén werknemer aanmaken, met alle velden meteen zichtbaar.
 *
 * Tot 22-08-2026 kon dit helemaal niet vanuit het scherm: een werknemer kwam er alleen in
 * doordat hij zich zelf aanmeldde via de link van zijn werkgever, of via een CSV-bestand
 * per werkgever. Dat werkt niet als iemand op de aanmeetdag spontaan langskomt.
 *
 * Het zzp-vinkje volgt het besluit van 22-08-2026: zzp'ers mogen tijdens de pilotfase mee.
 * De app maakt dan zelf een bedrijfsregel op zijn eigen naam — het bedrijf is verplicht in
 * de database en de hele app hangt eraan.
 */
function NieuweWerknemerModal({ werkgevers, gekozenWerkgever, onClose, onSubmit, loading }) {
  const [form, setForm] = useState({
    werkgever_id: gekozenWerkgever || "",
    zzp: false,
    kvk_nummer: "",
    voorletters: "", achternaam: "", geboortedatum: "",
    email: "", telefoon: "",
    straat: "", huisnummer: "", huisnummer_toevoeging: "", postcode: "", plaats: "",
  });
  const [getoond, setGetoond] = useState(false);

  function set(veld, waarde) {
    setForm((f) => ({ ...f, [veld]: waarde }));
  }

  /* Dezelfde eisen als de server stelt, zodat de beheerder ze hier al ziet in plaats van
     na het versturen. */
  const fouten = {};
  if (!form.zzp && !form.werkgever_id) fouten.werkgever_id = "Kies een werkgever, of vink aan dat het een zzp'er is.";
  if (!form.voorletters.trim()) fouten.voorletters = "Vul de voorletters in.";
  if (form.achternaam.trim().length < 2) fouten.achternaam = "Vul de achternaam in.";
  if (!form.geboortedatum) {
    fouten.geboortedatum = "Vul de geboortedatum in — die is nodig om dubbele aanmeldingen te herkennen.";
  }
  if (form.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
    fouten.email = "Vul een geldig e-mailadres in.";
  }
  if (form.postcode.trim() && !/^\d{4}\s?[A-Za-z]{2}$/.test(form.postcode.trim())) {
    fouten.postcode = "Een postcode ziet eruit als 1234 AB.";
  }
  if (form.zzp && form.kvk_nummer.trim() && !/^\d{8}$/.test(form.kvk_nummer.trim())) {
    fouten.kvk_nummer = "Een KVK-nummer bestaat uit acht cijfers.";
  }
  const compleet = Object.keys(fouten).length === 0;

  function versturen() {
    setGetoond(true);
    if (!compleet) return;
    const body = {};
    Object.entries(form).forEach(([veld, waarde]) => {
      if (veld === "zzp") return;
      const schoon = typeof waarde === "string" ? waarde.trim() : waarde;
      if (schoon !== "" && schoon != null) body[veld] = schoon;
    });
    if (form.zzp) {
      body.zzp = true;
      delete body.werkgever_id;
    } else {
      delete body.kvk_nummer;
    }
    onSubmit(body);
  }

  const RASTER = { display: "grid", gap: "var(--ruimte-5)", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" };
  const KOPJE = {
    font: "var(--text-label)", color: "var(--text-soft)", textTransform: "uppercase",
    letterSpacing: "0.06em", fontSize: "var(--fs-xs)", marginBottom: "var(--ruimte-4)",
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Nieuwe werknemer"
      data-testid="modal-werknemer-nieuw"
      className="fulkra"
      style={{
        position: "fixed", inset: 0, zIndex: 40, display: "flex", alignItems: "center",
        justifyContent: "center", padding: "var(--ruimte-7)", background: "var(--overlay)",
        overflowY: "auto",
      }}
    >
      <div style={{ width: "100%", maxWidth: 720, margin: "auto" }}>
        <Card title="Nieuwe werknemer">
          <p style={{ fontSize: "var(--fs-sm)", color: "var(--text-soft)", marginBottom: "var(--ruimte-6)" }}>
            Naam en geboortedatum zijn verplicht; de geboortedatum is nodig om te herkennen of
            iemand al bestaat. De rest kunt u later aanvullen.
          </p>

          <div style={{ display: "grid", gap: "var(--ruimte-7)" }}>
            <section>
              <div style={KOPJE}>Voor wie werkt deze persoon</div>
              <div style={{ display: "grid", gap: "var(--ruimte-4)" }}>
                <label
                  htmlFor="wn-zzp"
                  style={{ display: "flex", alignItems: "center", gap: "var(--ruimte-4)", fontSize: "var(--fs-body)" }}
                >
                  <input
                    id="wn-zzp"
                    type="checkbox"
                    checked={form.zzp}
                    onChange={(e) => set("zzp", e.target.checked)}
                    data-testid="modal-werknemer-zzp"
                    style={{ width: 18, height: 18 }}
                  />
                  <span>Zzp&apos;er — werkt voor zichzelf, geen bedrijf invullen</span>
                </label>

                {form.zzp ? (
                  <div style={RASTER}>
                    <WerknemerVeld
                      naam="kvk_nummer" label="KVK-nummer (mag later)" placeholder="12345678"
                      waarde={form.kvk_nummer} onChange={set} fout={getoond ? fouten.kvk_nummer : null}
                    />
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: "var(--ruimte-3)" }}>
                    <Label htmlFor="wn-werkgever" required>Werkgever</Label>
                    <select
                      id="wn-werkgever"
                      value={form.werkgever_id}
                      onChange={(e) => set("werkgever_id", e.target.value)}
                      data-testid="modal-werknemer-werkgever"
                      aria-invalid={getoond && fouten.werkgever_id ? true : undefined}
                      style={{
                        height: "var(--veld-hoogte-desktop)", borderRadius: "var(--radius)",
                        border: "1px solid var(--border-subtle)", background: "var(--wit)",
                        color: "var(--text-strong)", padding: "0 var(--ruimte-5)",
                        fontSize: "var(--fs-body)", fontFamily: "var(--font-tekst)",
                      }}
                    >
                      <option value="">— kies een werkgever —</option>
                      {werkgevers.map((w) => (
                        <option key={w.werkgever_id} value={w.werkgever_id}>{w.bedrijfsnaam}</option>
                      ))}
                    </select>
                    {getoond && fouten.werkgever_id ? (
                      <span data-testid="fout-werkgever_id" style={{ fontSize: "var(--fs-xs)", color: "var(--text-alarm)" }}>
                        {fouten.werkgever_id}
                      </span>
                    ) : null}
                  </div>
                )}
              </div>
            </section>

            <section>
              <div style={KOPJE}>De persoon</div>
              <div style={RASTER}>
                <WerknemerVeld naam="voorletters" label="Voorletters" verplicht placeholder="A.B" waarde={form.voorletters} onChange={set} fout={getoond ? fouten.voorletters : null} />
                <WerknemerVeld naam="achternaam" label="Achternaam" verplicht placeholder="de Vries" waarde={form.achternaam} onChange={set} fout={getoond ? fouten.achternaam : null} />
                <WerknemerVeld naam="geboortedatum" label="Geboortedatum" verplicht type="date" waarde={form.geboortedatum} onChange={set} fout={getoond ? fouten.geboortedatum : null} />
              </div>
            </section>

            <section>
              <div style={KOPJE}>Bereikbaarheid</div>
              <div style={RASTER}>
                <WerknemerVeld naam="email" label="E-mailadres" type="email" placeholder="naam@bedrijf.nl" waarde={form.email} onChange={set} fout={getoond ? fouten.email : null} />
                <WerknemerVeld naam="telefoon" label="Telefoonnummer" placeholder="0612345678" waarde={form.telefoon} onChange={set} fout={getoond ? fouten.telefoon : null} />
              </div>
            </section>

            <section>
              <div style={KOPJE}>Adres voor de levering</div>
              <div style={RASTER}>
                <WerknemerVeld naam="straat" label="Straat" placeholder="Dorpsstraat" waarde={form.straat} onChange={set} fout={null} />
                <WerknemerVeld naam="huisnummer" label="Huisnummer" placeholder="12" waarde={form.huisnummer} onChange={set} fout={null} />
                <WerknemerVeld naam="huisnummer_toevoeging" label="Toevoeging" placeholder="A" waarde={form.huisnummer_toevoeging} onChange={set} fout={null} />
                <WerknemerVeld naam="postcode" label="Postcode" placeholder="1234 AB" waarde={form.postcode} onChange={set} fout={getoond ? fouten.postcode : null} />
                <WerknemerVeld naam="plaats" label="Plaats" placeholder="Amersfoort" waarde={form.plaats} onChange={set} fout={null} />
              </div>
            </section>
          </div>

          <p
            data-testid="werknemer-toestemming-let-op"
            style={{
              marginTop: "var(--ruimte-6)", padding: "var(--ruimte-5)",
              borderRadius: "var(--radius)", border: "1px solid var(--border-subtle)",
              background: "var(--vlak-zacht, var(--paper))",
              fontSize: "var(--fs-sm)", color: "var(--text-body)",
            }}
          >
            <strong>Let op: toestemming.</strong> Meldt iemand zich zelf aan via de link van zijn
            werkgever, dan geeft hij daarbij zelf toestemming. Voert u hem hier in, dan gebeurt dat
            niet — zorg dat u die toestemming apart vastlegt voordat u hem de vragenlijst stuurt.
          </p>

          <div style={{ marginTop: "var(--ruimte-7)", display: "flex", justifyContent: "flex-end", gap: "var(--ruimte-4)" }}>
            <Button variant="secundair" onClick={onClose} data-testid="modal-werknemer-cancel">Annuleren</Button>
            <Button disabled={loading} onClick={versturen} data-testid="modal-werknemer-submit">
              {loading ? "Bezig…" : "Aanmaken"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
